/**
 * Narration Audio File Paths
 *
 * Centralized mapping of all narration audio files extracted from sprite-based system.
 * These individual MP3 files replace the previous large sprite files.
 *
 * Organized by phase:
 * - onboarding: Initial user experience narrations
 * - mission1-5: Mission-specific intros and level instructions
 * - clues: Real/decoy clue narrations for all missions
 * - system: Map navigation and UI narrations
 */

// Import all audio files from src/assets/narrations
// Onboarding (7 files)
import codeGeneration from "../assets/narrations/onboarding/code-generation.mp3";
import afterCodeGeneration from "../assets/narrations/onboarding/after-code-generation.mp3";
import welcomeBoard from "../assets/narrations/onboarding/welcome-board-intro.mp3";
import levelRookie from "../assets/narrations/onboarding/level-rookie.mp3";
import levelIntermediate from "../assets/narrations/onboarding/level-intermediate.mp3";
import levelAdvanced from "../assets/narrations/onboarding/level-advanced.mp3";
import objectiveExplanation from "../assets/narrations/onboarding/objective-explanation.mp3";

// Mission 1 (12 files)
import m1Intro from "../assets/narrations/mission1/m1-intro.mp3";
import m1IntroSec from "../assets/narrations/mission1/m1-intro-sec.mp3";
import m1LevelSelection from "../assets/narrations/mission1/m1-level-selection.mp3";
import m1InstructionsIntro from "../assets/narrations/mission1/m1-instructions-intro.mp3";
import m1ControlHints from "../assets/narrations/mission1/m1-control-hints.mp3";
import m1ControlSave from "../assets/narrations/mission1/m1-control-save.mp3";
import m1ControlStop from "../assets/narrations/mission1/m1-control-stop.mp3";
import m1ControlBack from "../assets/narrations/mission1/m1-control-back.mp3";
import m1GoodLuck from "../assets/narrations/mission1/m1-good-luck.mp3";
import m1RookieInstructions from "../assets/narrations/mission1/m1-rookie-instructions.mp3";
import m1IntermediateInstructions from "../assets/narrations/mission1/m1-intermediate-instructions.mp3";
import m1AdvancedInstructions from "../assets/narrations/mission1/m1-advanced-instructions.mp3";

// Mission 2 (4 files)
import m2Intro from "../assets/narrations/mission2/m2-intro.mp3";
import m2RookieInstructions from "../assets/narrations/mission2/m2-rookie-instructions.mp3";
import m2IntermediateInstructions from "../assets/narrations/mission2/m2-intermediate-instructions.mp3";
import m2AdvancedInstructions from "../assets/narrations/mission2/m2-advanced-instructions.mp3";

// Mission 3 (4 files)
import m3Intro from "../assets/narrations/mission3/m3-intro.mp3";
import m3RookieInstructions from "../assets/narrations/mission3/m3-rookie-instructions.mp3";
import m3IntermediateInstructions from "../assets/narrations/mission3/m3-intermediate-instructions.mp3";
import m3AdvancedInstructions from "../assets/narrations/mission3/m3-advanced-instructions.mp3";

// Mission 4 (4 files)
import m4Intro from "../assets/narrations/mission4/m4-intro.mp3";
import m4RookieInstructions from "../assets/narrations/mission4/m4-rookie-instructions.mp3";
import m4IntermediateInstructions from "../assets/narrations/mission4/m4-intermediate-instructions.mp3";
import m4AdvancedInstructions from "../assets/narrations/mission4/m4-advanced-instructions.mp3";

// System (5 files)
import mapIntro from "../assets/narrations/system/map-intro.mp3";
import mapHoverLevel from "../assets/narrations/system/map-hover-level.mp3";
import mapSettingsIntro from "../assets/narrations/system/map-settings-intro.mp3";
import mapClueBoard from "../assets/narrations/system/map-clue-board.mp3";
import mapSignOut from "../assets/narrations/system/map-sign-out.mp3";

// Exit Flow (1 file)
import gameFinishP1 from "../assets/narrations/exit/game-finish-p1.mp3";
import gameFinishP2 from "../assets/narrations/exit/game-finish-p2.mp3";

// Clues (30 files)
import m1RookieClueReal from "../assets/narrations/clues/m1-rookie-clue-real.mp3";
import m1RookieClueDecoy from "../assets/narrations/clues/m1-rookie-clue-decoy.mp3";
import m1IntermediateClueReal from "../assets/narrations/clues/m1-intermediate-clue-real.mp3";
import m1IntermediateClueDecoy from "../assets/narrations/clues/m1-intermediate-clue-decoy.mp3";
import m1AdvancedClueReal from "../assets/narrations/clues/m1-advanced-clue-real.mp3";
import m1AdvancedClueDecoy from "../assets/narrations/clues/m1-advanced-clue-decoy.mp3";

import m2RookieClueReal from "../assets/narrations/clues/m2-rookie-clue-real.mp3";
import m2RookieClueDecoy from "../assets/narrations/clues/m2-rookie-clue-decoy.mp3";
import m2IntermediateClueReal from "../assets/narrations/clues/m2-intermediate-clue-real.mp3";
import m2IntermediateClueDecoy from "../assets/narrations/clues/m2-intermediate-clue-decoy.mp3";
import m2AdvancedClueReal from "../assets/narrations/clues/m2-advanced-clue-real.mp3";
import m2AdvancedClueDecoy from "../assets/narrations/clues/m2-advanced-clue-decoy.mp3";

import m3RookieClueReal from "../assets/narrations/clues/m3-rookie-clue-real.mp3";
import m3RookieClueDecoy from "../assets/narrations/clues/m3-rookie-clue-decoy.mp3";
import m3IntermediateClueReal from "../assets/narrations/clues/m3-intermediate-clue-real.mp3";
import m3IntermediateClueDecoy from "../assets/narrations/clues/m3-intermediate-clue-decoy.mp3";
import m3AdvancedClueReal from "../assets/narrations/clues/m3-advanced-clue-real.mp3";
import m3AdvancedClueDecoy from "../assets/narrations/clues/m3-advanced-clue-decoy.mp3";

import m4RookieClueReal from "../assets/narrations/clues/m4-rookie-clue-real.mp3";
import m4RookieClueDecoy from "../assets/narrations/clues/m4-rookie-clue-decoy.mp3";
import m4IntermediateClueReal from "../assets/narrations/clues/m4-intermediate-clue-real.mp3";
import m4IntermediateClueDecoy from "../assets/narrations/clues/m4-intermediate-clue-decoy.mp3";
import m4AdvancedClueReal from "../assets/narrations/clues/m4-advanced-clue-real.mp3";
import m4AdvancedClueDecoy from "../assets/narrations/clues/m4-advanced-clue-decoy.mp3";

export const NARRATION_PATHS = {
  // ONBOARDING (7 files)
  onboarding: {
    codeGeneration,
    afterCodeGeneration,
    welcomeBoard,
    levelRookie,
    levelIntermediate,
    levelAdvanced,
    objectiveExplanation,
  },

  // MISSION 1: Secrets of the Circuit (12 files)
  mission1: {
    intro: m1Intro,
    introSec: m1IntroSec,
    levelSelection: m1LevelSelection,
    instructionsIntro: m1InstructionsIntro,
    controlHints: m1ControlHints,
    controlSave: m1ControlSave,
    controlStop: m1ControlStop,
    controlBack: m1ControlBack,
    goodLuck: m1GoodLuck,
    rookieInstructions: m1RookieInstructions,
    intermediateInstructions: m1IntermediateInstructions,
    advancedInstructions: m1AdvancedInstructions,
  },

  // MISSION 2: Access Denied (4 files)
  mission2: {
    intro: m2Intro,
    rookieInstructions: m2RookieInstructions,
    intermediateInstructions: m2IntermediateInstructions,
    advancedInstructions: m2AdvancedInstructions,
  },

  // MISSION 3: Digital Breadcrumbs (4 files)
  mission3: {
    intro: m3Intro,
    rookieInstructions: m3RookieInstructions,
    intermediateInstructions: m3IntermediateInstructions,
    advancedInstructions: m3AdvancedInstructions,
  },

  // MISSION 4: Visual Decipher (4 files)
  mission4: {
    intro: m4Intro,
    rookieInstructions: m4RookieInstructions,
    intermediateInstructions: m4IntermediateInstructions,
    advancedInstructions: m4AdvancedInstructions,
  },

  // CLUES: Real and Decoy clues for all missions (30 files)
  clues: {
    // Mission 1 clues (6 files)
    m1RookieReal: m1RookieClueReal,
    m1RookieDecoy: m1RookieClueDecoy,
    m1IntermediateReal: m1IntermediateClueReal,
    m1IntermediateDecoy: m1IntermediateClueDecoy,
    m1AdvancedReal: m1AdvancedClueReal,
    m1AdvancedDecoy: m1AdvancedClueDecoy,

    // Mission 2 clues (6 files)
    m2RookieReal: m2RookieClueReal,
    m2RookieDecoy: m2RookieClueDecoy,
    m2IntermediateReal: m2IntermediateClueReal,
    m2IntermediateDecoy: m2IntermediateClueDecoy,
    m2AdvancedReal: m2AdvancedClueReal,
    m2AdvancedDecoy: m2AdvancedClueDecoy,

    // Mission 3 clues (6 files)
    m3RookieReal: m3RookieClueReal,
    m3RookieDecoy: m3RookieClueDecoy,
    m3IntermediateReal: m3IntermediateClueReal,
    m3IntermediateDecoy: m3IntermediateClueDecoy,
    m3AdvancedReal: m3AdvancedClueReal,
    m3AdvancedDecoy: m3AdvancedClueDecoy,

    // Mission 4 clues (6 files)
    m4RookieReal: m4RookieClueReal,
    m4RookieDecoy: m4RookieClueDecoy,
    m4IntermediateReal: m4IntermediateClueReal,
    m4IntermediateDecoy: m4IntermediateClueDecoy,
    m4AdvancedReal: m4AdvancedClueReal,
    m4AdvancedDecoy: m4AdvancedClueDecoy,
  },

  // SYSTEM: Map navigation and UI narrations (5 files)
  system: {
    mapIntro,
    mapHoverLevel,
    mapSettingsIntro,
    mapClueBoard,
    mapSignOut,
  },

  // EXIT FLOW: End game narrations (1 file)
  exit: {
    gameFinishP1,
    gameFinishP2,
  },
};

/**
 * Helper function to get narration path by category and key
 * @param {string} category - Category (e.g., 'mission1', 'clues')
 * @param {string} key - Key within category (e.g., 'intro', 'm1RookieReal')
 * @returns {string} Full path to audio file
 */
export const getNarrationPath = (category, key) => {
  if (!NARRATION_PATHS[category]) {
    console.warn(`Narration category "${category}" not found`);
    return null;
  }
  if (!NARRATION_PATHS[category][key]) {
    console.warn(`Narration key "${key}" not found in category "${category}"`);
    return null;
  }
  return NARRATION_PATHS[category][key];
};

/**
 * Get all narration paths as flat array for preloading
 * @returns {string[]} Array of all narration file paths
 */
export const getAllNarrationPaths = () => {
  const paths = [];
  Object.values(NARRATION_PATHS).forEach((category) => {
    Object.values(category).forEach((path) => {
      paths.push(path);
    });
  });
  return paths;
};

/**
 * Get critical narrations to preload on app initialization
 * These are played during onboarding and initial map experience
 * @returns {string[]} Array of critical narration paths
 */
export const getCriticalNarrationPaths = () => {
  return [
    // Onboarding narrations
    ...Object.values(NARRATION_PATHS.onboarding),
    // System/map narrations
    ...Object.values(NARRATION_PATHS.system),
  ];
};

/**
 * Get mission-specific narration paths
 * @param {number} missionNumber - Mission number (1-5)
 * @returns {string[]} Array of narration paths for the mission
 */
export const getMissionNarrationPaths = (missionNumber) => {
  const missionKey = `mission${missionNumber}`;
  if (!NARRATION_PATHS[missionKey]) {
    console.warn(`Mission ${missionNumber} narrations not found`);
    return [];
  }
  return Object.values(NARRATION_PATHS[missionKey]);
};

/**
 * Get clue narration paths for a specific mission and level
 * @param {number} missionNumber - Mission number (1-5)
 * @param {string} level - Level ('rookie', 'intermediate', 'advanced')
 * @param {boolean} isReal - True for real clue, false for decoy
 * @returns {string} Path to clue narration
 */
export const getCluePath = (missionNumber, level, isReal) => {
  const levelCap = level.charAt(0).toUpperCase() + level.slice(1);
  const clueType = isReal ? "Real" : "Decoy";
  const key = `m${missionNumber}${levelCap}${clueType}`;
  return NARRATION_PATHS.clues[key] || null;
};
